export type APIError = {
  errors: {};
  status: number;
  title: string;
  type: string;
};

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return typeof error === 'object' && error !== null && 'message' in error;
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

export function ErrorHandler(errors: any) {
  let errorMessage = '';
  Object.values(errors).forEach((error: any) => (errorMessage += `${error[0]}\n`));
  return errorMessage;
}

export function APIResponseHandler(response) {
  if (response.data === '' && (response.status === 200 || response.status === 204)) {
    return true;
  }
  
  // API returns 404 for empty list of users in organization or no user existing for given email
  if (response.status >= 400 && response.status != 404) {
    const method = response?.config?.method?.toUpperCase() || 'UNKNOWN METHOD';
    const url = response?.config?.url || response?.request?.responseURL || 'unknown route';
    
    let errorDetail = '';
    if (response.data) {
      if (typeof response.data === 'string') {
        try {
          const parsed = JSON.parse(response.data);
          errorDetail = JSON.stringify(parsed, null, 2);
        } catch {
          errorDetail = response.data;
        }
      } else if (response.data.message) {
        errorDetail = response.data.message;
      } else if (response.data.title || response.data.detail) {
        errorDetail = `${response.data.title || ''} ${response.data.detail || ''}`.trim();
      } else {
        try {
          errorDetail = JSON.stringify(response.data, null, 2);
        } catch {
          errorDetail = '';
        }
      }
    }
    
    const errorMessage = errorDetail 
      ? `Failed to perform a network request:\n\nRequest: ${method} ${url}\nStatus: ${response.statusText} (${response.status})\n\nDetails:\n${errorDetail}`
      : `Failed to perform a network request:\n\nRequest: ${method} ${url}\nStatus: ${response.statusText} (${response.status})`;
    
    throw new Error(errorMessage);
  } else {
    return response.data;
  }
}

export function storeResponseHandler(resp: any, successMessage: string, addToast: any) {
  if (resp === true || (resp.data && !resp.isError)) {
    addToast('Success', successMessage, 'success');
  }
  if ((resp as APIError).errors) {
    addToast((resp as APIError).title, ErrorHandler((resp as APIError).errors), 'error');
  }
}
